import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
  MutationCache,
} from '@tanstack/react-query';
import { useEffect, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { setErrorTranslator, toApiError } from '@/api/errorHandler';
import { notifyGlobal } from '@/context/notificationContext';

interface AppQueryProviderProps {
  children: ReactNode;
}

export function AppQueryProvider({ children }: AppQueryProviderProps) {
  const { t } = useTranslation('entity-portal');

  useEffect(() => {
    setErrorTranslator(t);
  }, [t]);

  const queryClient = useMemo(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error) => {
            const apiError = toApiError(error, t);
            notifyGlobal(apiError.message, 'error');
          },
        }),
        mutationCache: new MutationCache({
          onError: (error) => {
            const apiError = toApiError(error, t);
            notifyGlobal(apiError.message, 'error');
          },
        }),
        defaultOptions: {
          queries: {
            retry: 1,
            refetchOnWindowFocus: false,
          },
        },
      }),
    [t],
  );

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
}
